<script setup lang="ts">
/**
 * A summary button that shows and hides the content below it — „3 unauffällige
 * Regeln anzeigen", „Regelwerk anzeigen", „▸ 2 Antworten".
 *
 * A button with `aria-expanded` / `aria-controls` (the APG disclosure pattern)
 * rather than a native `<details>`: the open state belongs to the caller
 * (`v-model:open`), and a `<summary>` cannot state its own expanded state. The
 * content stays mounted while closed (`v-show`), so `aria-controls` always
 * points at an element and a half-typed reply survives folding it away.
 *
 * `NxPanel` is a surface, not a toggle — it has no collapsed state.
 */
import { ChevronRightIcon } from '@blpde/nx-icons';
import { useId } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Summary text; the `#summary` slot replaces it. */
    summary?: string;
    /**
     * Chevron before the summary (default) or after it — `end` suits rows that
     * carry a count or a legend dot and read like a list entry.
     */
    chevronPosition?: 'start' | 'end';
    /** The summary sizes to its text instead of spanning the full width. */
    inline?: boolean;
    disabled?: boolean;
  }>(),
  { summary: '', chevronPosition: 'start', inline: false, disabled: false }
);

const open = defineModel<boolean>('open', { default: false });

defineSlots<{
  /** Replaces the summary text; gets the open state for „anzeigen"/„ausblenden". */
  summary?: (props: { open: boolean }) => unknown;
  default?: () => unknown;
}>();

const contentId = useId();

function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
}
</script>

<template>
  <div
    class="nx-disclosure"
    :class="{
      'is-open': open,
      'is-inline': props.inline,
      'chevron-end': props.chevronPosition === 'end',
    }"
  >
    <button
      type="button"
      class="nx-disclosure-summary"
      :aria-expanded="open"
      :aria-controls="contentId"
      :disabled="props.disabled"
      @click="toggle"
    >
      <ChevronRightIcon filled class="nx-disclosure-chevron nxt-icon-sm" aria-hidden="true" />
      <span class="nx-disclosure-label">
        <slot name="summary" :open="open">{{ props.summary }}</slot>
      </span>
    </button>
    <div v-show="open" :id="contentId" class="nx-disclosure-content">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-disclosure {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.nx-disclosure.is-inline {
  align-items: flex-start;
}

.nx-disclosure-summary {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  width: 100%;
  min-width: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  text-align: start;
  cursor: pointer;
}
.is-inline .nx-disclosure-summary {
  width: auto;
}
.nx-disclosure-summary:hover:not(:disabled) {
  color: var(--nxt-fg);
  background: var(--nxt-surface-sunken-bg);
}
.nx-disclosure-summary:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-disclosure-summary:disabled {
  color: var(--nxt-disabled-fg);
  cursor: not-allowed;
}

.nx-disclosure-label {
  flex: 1 1 auto;
  min-width: 0;
}
.is-inline .nx-disclosure-label {
  flex: 0 1 auto;
}

/* One glyph turned a quarter, not two icons swapped: ▸ closed, ▾ open. */
.nx-disclosure-chevron {
  flex: 0 0 auto;
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
/* Its own chevron only: a disclosure inside an open one keeps its own state. */
.is-open > .nx-disclosure-summary > .nx-disclosure-chevron {
  transform: rotate(90deg);
}
/* At the end the chevron follows the label — after a count, like a list row. */
.chevron-end .nx-disclosure-chevron {
  order: 1;
}

.nx-disclosure-content {
  margin-block-start: var(--nxt-space-2xs);
}

@media (prefers-reduced-motion: reduce) {
  .nx-disclosure-chevron {
    transition: none;
  }
}
</style>
